import React, { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardFooter, CardHeader } from "@/components/ui/card";
import { PostType } from "@/types/post";
import { PostHeader } from "./PostHeader";
import { PollComponent } from "./PollComponent";
import { QuotedPost } from "./QuotedPost";
import { MediaGrid } from "./MediaGrid";
import { PostActions } from "./PostActions";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { getSocket } from "@/socket";
import notify from "@/lib/notify";
import { getPostReplies } from "@/actions/posts/index";

interface PostDetailProps {
  post: PostType;
  currentUser: any;
  isLoadingDetails?: boolean;
}

export function PostDetail({ post: initialPost, currentUser, isLoadingDetails = false }: PostDetailProps) {
  const [post, setPost] = useState(initialPost);
  const [replyContent, setReplyContent] = useState("");
  const [loadingMoreReplies, setLoadingMoreReplies] = useState(false);
  const { toast } = useToast();
  const socket = getSocket();
  const formattedDate = format(new Date(post.createdAt), "h:mm a · MMM d, yyyy");
  const formattedViews = post.viewCount > 1000000 
    ? `${(post.viewCount / 1000000).toFixed(1)}M`
    : post.viewCount > 1000
    ? `${(post.viewCount / 1000).toFixed(1)}K`
    : post.viewCount;

  const loadMoreReplies = async () => {
    try {
      setLoadingMoreReplies(true);
      const currentRepliesCount = post?.replies?.length;
      const newReplies = await getPostReplies(post.id, currentRepliesCount);
      
      if (newReplies.length > 0) {
        setPost(prev => ({
          ...prev,
          replies: [...prev.replies, ...newReplies]
        }));
      }
    } catch (error) {
      console.error('Error loading more replies:', error);
      toast({
        title: "Error",
        description: "Failed to load more replies",
        variant: "destructive",
      });
    } finally {
      setLoadingMoreReplies(false);
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim() || !currentUser || !socket) {
      toast({
        title: "Error",
        description: "Please write something to reply",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await fetch(`${process?.env?.NEXT_PUBLIC_API_URL}/posts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          content: replyContent,
          type: "reply",
          originalPostId: post.id,
          files: [],
          isThread: true,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const newReply = await response.json();
      
      // Ensure reply has all required fields
      const completeReply = {
        id: newReply.id,
        content: replyContent,
        createdAt: new Date(),
        userId: currentUser.id,
        name: currentUser.name,
        username: currentUser.username,
        image: currentUser.image,
        files: [],
        likes: [],
        reposts: [],
        postId: post.id.toString()
      };

      setPost(prevPost => ({
        ...prevPost,
        replies: [completeReply, ...prevPost.replies]
      }));

      setReplyContent("");

      toast({
        title: "Success",
        description: "Reply posted successfully",
      });

      socket.emit("post", { ...newReply, isThread: true });
    } catch (error) {
      console.error("Error posting reply:", error);
      toast({
        title: "Error",
        description: "Failed to post reply",
        variant: "destructive",
      });
    }
  };

  const handleLike = async (replyId?: string | number) => {
    if (!currentUser) {
      toast({
        title: "Error",
        description: "Please log in to like",
        variant: "destructive",
      });
      return;
    }

    try {
      const targetId = replyId || post.id;
      const targetType = replyId ? 'reply' : 'post';
      const response = await fetch(`${process?.env?.NEXT_PUBLIC_API_URL}/${targetType}s/${targetId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          likes: replyId 
            ? post?.replies?.find(r => r.id === replyId)?.likes?.includes(currentUser.id)
              ? { disconnect: currentUser.id }
              : { connect: currentUser.id }
            : post.likes?.includes(currentUser.id)
              ? { disconnect: currentUser.id }
              : { connect: currentUser.id }
        }),
      });

      if (response.ok) {
        setPost(prevPost => {
          if (replyId) {
            return {
              ...prevPost,
              replies: prevPost?.replies?.map(reply => {
                if (reply.id === replyId) {
                  const isLiked = reply.likes?.includes(currentUser.id);
                  return {
                    ...reply,
                    likes: isLiked
                      ? reply?.likes?.filter(id => id !== currentUser.id)
                      : [...(reply.likes || []), currentUser.id]
                  };
                }
                return reply;
              })
            };
          } else {
            const isLiked = prevPost.likes?.includes(currentUser.id);
            return {
              ...prevPost,
              likes: isLiked
                ? prevPost?.likes?.filter(id => id !== currentUser.id)
                : [...(prevPost.likes || []), currentUser.id]
            };
          }
        });

        if (!replyId) {
          notify({
            userId: post.userId,
            type: "like",
            message: `${currentUser.name} liked your post`,
          });
        }
      }
    } catch (error) {
      console.error('Error handling like:', error);
      toast({
        title: "Error",
        description: "Failed to like",
        variant: "destructive",
      });
    }
  };

  const handleBookmark = async () => {
    if (!currentUser) {
      toast({
        title: "Error",
        description: "Please log in to bookmark",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await fetch(`${process?.env?.NEXT_PUBLIC_API_URL}/bookmarks`, {
        method: post.bookmarks?.some(b => b.userId === currentUser.id) ? 'DELETE' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postId: post.id.toString() }),
      });

      if (response.ok) {
        setPost(prevPost => {
          const isBookmarked = prevPost.bookmarks?.some(b => b.userId === currentUser.id);
          return {
            ...prevPost,
            bookmarks: isBookmarked
              ? (prevPost.bookmarks || []).filter(b => b.userId !== currentUser.id)
              : [...(prevPost.bookmarks || []), { id: String(Date.now()), userId: String(currentUser.id), postId: String(post.id) }]
          };
        });

        toast({
          title: "Success",
          description: post.bookmarks?.some(b => b.userId === currentUser.id)
            ? "Removed from bookmarks"
            : "Added to bookmarks",
        });
      }
    } catch (error) {
      console.error('Error handling bookmark:', error);
      toast({
        title: "Error",
        description: "Failed to update bookmark",
        variant: "destructive",
      });
    }
  };

  const handleRepost = async (replyId?: string | number) => {
    if (!currentUser) {
      toast({
        title: "Error",
        description: "Please log in to repost",
        variant: "destructive",
      });
      return;
    }

    try {
      const targetId = replyId || post.id;
      const targetType = replyId ? 'reply' : 'post';
      const response = await fetch(`${process?.env?.NEXT_PUBLIC_API_URL}/${targetType}s/${targetId}/repost`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: currentUser.id }),
      });

      if (response.ok) {
        setPost(prevPost => {
          if (replyId) {
            return {
              ...prevPost,
              replies: prevPost?.replies?.map(reply => {
                if (reply.id === replyId) {
                  const isReposted = reply.reposts?.includes(currentUser.id);
                  return {
                    ...reply,
                    reposts: isReposted
                      ? reply?.reposts?.filter(id => id !== currentUser.id)
                      : [...(reply.reposts || []), currentUser.id]
                  };
                }
                return reply;
              })
            };
          } else {
            const isReposted = prevPost.reposts?.includes(currentUser.id);
            return {
              ...prevPost,
              reposts: isReposted
                ? prevPost?.reposts?.filter(id => id !== currentUser.id)
                : [...(prevPost.reposts || []), currentUser.id]
            };
          }
        });

        toast({
          title: "Success",
          description: "Reposted successfully",
        });
      }
    } catch (error) {
      console.error('Error handling repost:', error);
      toast({
        title: "Error",
        description: "Failed to repost",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <Card className="relative">
        <CardHeader className="flex flex-row space-x-4 items-start p-4">
          <Avatar className="h-10 w-10 flex-shrink-0">
            <AvatarImage src={post.image || "/default-image.png"} />
            <AvatarFallback>{post.name[0]}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col w-full">
            <PostHeader
              name={post.name}
              username={post.username}
              image={post.image}
              createdAt={post.createdAt}
              currentUserId={currentUser?.id}
              userId={post.userId}
              isFollowing={false}
              targetUser={currentUser}
              onFollowToggle={() => {}}
              onDelete={() => {}}
            />

            <p className="mt-5 mb-2 whitespace-pre-wrap">{post.content}</p>

            {post.poll && (
              <PollComponent
                poll={post.poll}
                hasVoted={false}
                pollResults={{}}
                totalVotes={0}
                onVote={async () => {}}
              />
            )}

            {post.type === "quote" && post.originalPostId && post.Post && (
              <QuotedPost
                post={post.Post}
                originalPostId={post.originalPostId}
              />
            )}

            {post.medias && post?.medias?.length > 0 && (
              <MediaGrid
                medias={post.medias}
                postId={post.id}
              />
            )}
          </div>
        </CardHeader>

        <CardFooter className="flex flex-col gap-4 px-6 relative z-10">
          <PostActions
            postId={post.id}
            replyCount={post.replies?.length || 0}
            likeCount={post.likes?.length || 0}
            repostCount={post.reposts?.length || 0}
            isLiked={post.likes?.includes(currentUser?.id)}
            isReposted={post.reposts?.includes(currentUser?.id)}
            bookmarkCount={post.bookmarks?.length || 0}
            viewCount={post.viewCount}
            isBookmarked={post.bookmarks?.some(b => b.userId === currentUser?.id) || false}
            onLike={() => handleLike()}
            onRepost={() => handleRepost()}
            onQuote={() => {}}
            onBookmark={handleBookmark}
            onCopyLink={() => {}}
            onShare={() => {}}
            onSendDM={() => {}}
          />
          <div className="text-sm text-gray-500 border-t border-gray-200 mt-2 pt-4">
            {formattedDate} · {formattedViews} Views
          </div>
        </CardFooter>
      </Card>

      {/* Reply Section */}
      <Card className="relative">
        <CardHeader className="flex flex-row space-x-4 items-start p-4">
          <Avatar className="h-10 w-10 flex-shrink-0">
            <AvatarImage src={currentUser?.image || undefined} />
            <AvatarFallback>{currentUser?.name?.[0]}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col w-full">
            <textarea
              className="w-full p-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Post your reply..."
              value={replyContent}
              onChange={(e) => setReplyContent(e?.target?.value)}
              rows={3}
            />
            <div className="mt-2 flex justify-end">
              <Button 
                onClick={handleReply}
                disabled={!replyContent.trim()}
              >
                Reply
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Replies List */}
      {isLoadingDetails ? (
        <div className="flex justify-center p-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : post.replies?.length > 0 && (
        <div className="space-y-4">
          {post?.replies?.map((reply) => (
            <Card key={reply.id} className="relative">
              <CardHeader className="flex flex-row space-x-4 items-start p-4">
                <Avatar className="h-10 w-10 flex-shrink-0">
                  <AvatarImage src={reply.image || undefined} />
                  <AvatarFallback>{reply.name[0]}</AvatarFallback>
                </Avatar>
                <div className="flex flex-col w-full">
                  <PostHeader
                    name={reply.name}
                    username={reply.username}
                    image={reply.image}
                    createdAt={reply.createdAt}
                    currentUserId={currentUser?.id}
                    userId={reply.userId}
                    isFollowing={false}
                    targetUser={currentUser}
                    onFollowToggle={() => {}}
                    onDelete={() => {}}
                  />
                  <p className="mt-2 whitespace-pre-wrap">{reply.content}</p>
                </div>
              </CardHeader>
              <CardFooter className="flex flex-col gap-4 px-6 relative z-10">
                <PostActions
                  postId={reply.id}
                  replyCount={0}
                  likeCount={reply.likes?.length || 0}
                  repostCount={reply.reposts?.length || 0}
                  bookmarkCount={0}
                  viewCount={0}
                  isLiked={reply.likes?.includes(currentUser?.id)}
                  isReposted={reply.reposts?.includes(currentUser?.id)}
                  isBookmarked={false}
                  onLike={() => handleLike(reply.id)}
                  onRepost={() => handleRepost(reply.id)}
                  onQuote={() => {}}
                  onBookmark={() => {}}
                  onCopyLink={() => {}}
                  onShare={() => {}}
                  onSendDM={() => {}}
                />
              </CardFooter>
            </Card>
          ))}
          
          {post?.replies?.length >= 5 && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={loadMoreReplies}
                disabled={loadingMoreReplies}
                className="mt-4"
              >
                {loadingMoreReplies ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                ) : (
                  "Show More Replies"
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}